<template>
	<div class="graph-nd graph-2d">
		<svg :viewBox="'0 0 ' + width + ' ' + height" xmlns="http://www.w3.org/2000/svg">
			<Legend v-bind:width="width"
				v-bind:height="height"
				v-bind:startDate="startDate"
				v-bind:bins="bins.length"
				v-bind:binWidth="binWidth">
			</Legend>

			<g v-for="(bin, index) in bins" :key="index"
				class="tooltip-trigger"
				@click="showTooltip(bin, ...arguments)"
				@mouseover="showTooltip(bin, ...arguments)"
				@mouseout="hideTooltip">

				<template v-if="bin.length == 1">
					<circle
						:cx="centerScaleX(index)"
						:cy="scaleY(bin[0].value) - 8"
						r="10"
						:style="{ fill: '#ffffff' }">
					</circle>
					<text :x="centerScaleX(index)"
						  :y="scaleY(bin[0].value) - 5"
						  :style="style.barLabelFont"
	  					  text-anchor="middle">
						  {{ bin[0].value }}
					</text>
					<rect   :fill="style.barColor"
							:x="centerScaleX(index) - style.barWidth / 2"
							:y="scaleY(bin[0].value)"
							:width="style.barWidth"
							:height="scaleHeight(bin[0].value)">
					</rect>
				</template>

				<template v-else-if="bin.length > 1">
					<circle
						:cx="centerScaleX(index)"
						:cy="scaleY(binAverage(bin)) - 8"
						r="10"
						:style="{ fill: '#ffffff' }">
					</circle>
					<text :x="centerScaleX(index)"
						  :y="scaleY(binAverage(bin)) - 5"
						  :style="style.barLabelFont"
	  					  text-anchor="middle">
						  {{ formatAverage(binAverage(bin)) }}
					</text>
					<rect   :fill="style.barColor"
							:x="centerScaleX(index) - style.barWidth / 2"
							:y="scaleY(binAverage(bin))"
							:width="style.barWidth"
							:height="scaleHeight(binAverage(bin))">
					</rect>
				</template>

			</g>
		</svg>

		<Tooltip v-bind:display="tooltipDisplay" @hover="hoverTooltip">
			<p v-for="(point, i) in tooltipBin" :key="i">
				<strong>Stress Level: {{ withPrecision(point.value, precision) }}</strong><br>
				<small>{{ formatDateTime(point.time) }}</small>
			</p>
		</Tooltip>
	</div>
</template>

<script>
import Graph from '.'

export default {
	name: 'bar-graph-2d',
	extends: Graph,

	props: {
		barHeight: {
			type: Number,
			default: 4
		},
		precision: {
			type: Number,
			default: 0
		}

	},

	computed: {
		/**
		 * @function 	bins
		 * @desc 		Bins the data points into day-wide bins for the interval determined by the current scale
		 */
		bins() {
			var bins = []

			for (var t = this.startDate.getTime() ; t <= this.date.getTime() ; t += this.binInterval) {
				bins.push(this.data.filter((point) =>
					(t <= point.time && point.time <= t + this.binInterval)
				).sort((a, b) => {
					return a.time - b.time
				}))
			}

			return bins
		},

		/**
		 * @function 	maxValue
		 * @desc 		maximum value that a point has among four values of diastolic and systolic readings
		 */
		maxValue() {
			return Math.max.apply(null, this.data.map((point) => point.value))
		},

		/**
		 * @function 	minValue
		 * @desc 		minimum value that a point has among four values of diastolic and systolic readings
		 */
		minValue() {
			return Math.min.apply(null, this.data.map((point) => point.value))
		},

		/**
		 * @function 	range
		 * @desc 		maximum difference among all four values of BP
		 */
		range() {
			return Math.max(this.maxValue - this.minValue, 0)
		},

		/**
		 * @function 	displayHeight
		 * @desc 		height of graph bars
		 */
		displayHeight() {
			return this.height - this.style.padding * 2 - this.style.legendHeight - 30
		},

		/**
		 * @function 	legendStart
		 * @desc 		first x-axis point to plot value at
		 */
		legendStart() {
			return this.height - this.style.padding * 2 - this.style.legendHeight
		}
	},
	methods: {
		/**
		 * @function 	binPortionAverage
		 * @desc 		Returns the ceil of the portion size values of each point in a bin.
		 * @param 		{Array} bin
		 */
		binAverage(bin) {
			if (bin.length == 0)
				return 0
			return 1.0 * bin.map((point) => point.value).reduce((a, b) => (a + b)) / bin.length
		},

		/**
		 * @function 	formatAverage
		 * @desc 		
		 * @param 		{Number}
		 */
		formatAverage(average) {
			if (Math.abs(average - Math.round(average)) < 0.1)
				return Math.round(average)
			return average.toFixed(1)
		},
		setScale(scale) {
			this.scale = scale
		},
		scaleX(x) {
			return x * this.width / this.bins.length
		},

		/**
		 * @function 	scaleY
		 * @desc 		scaling the points on graph w.r.t the barHeight (taking barHeight as maximum value that a point should take)
		 * @param 		{y} Number
		 */
		scaleY(y) {
			return (this.barHeight - y) / this.barHeight * this.displayHeight + this.style.padding
		},
		
		/**
		 * @function 	scaleHeight
		 * @desc 		scaling the height of graph w.r.t the barHeight (taking barHeight as maximum value that a point should take)
		 * @param 		{y} Number
		 */
		scaleHeight(y) {
			return y / this.barHeight * this.displayHeight
		},

		/**
		 * @function 	centerScaleX
		 * @desc 		
		 * @param 		{x} Number
		 */
		centerScaleX(x) {
			return (x + 0.5) * this.width / (this.bins.length + 1)
		},

		/**
		 * @function 	withPrecision
		 * @desc 		rounding the given value w.r.t the precision
		 * @param 		{value} Number
		 */
		withPrecision(value) {
			if (this.precision == 0 || Math.abs(value - Math.round(value)) < 0.01) {
				return Math.round(value)
			}
			else {
				return value.toFixed(this.precision)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.graph-2d {
	background-color: #ffffff;
	position: relative;
}
svg {
	width: 100%;
	height: auto;
}

</style>